import React, { useEffect, useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import {
  buildOptionsFromData,
  DateRangeFilter,
  ErrorHandler,
  FilteredTable,
  Link,
  MultiSelectTableFilter,
  NumberRangeFilter,
  Text,
  TextFilterSearch,
  WorksAvatar,
} from '@common/components';
import {
  FetchMoreType,
  usePaginationHelper,
  useQuery,
  useNavigate,
} from '@common/hooks';
import { Colors } from '@common/tokens';
import { Spacing } from 'works/legacy/lib/style-helper';
import { ActiveCtaButton } from 'works/legacy/pages/supplier/staffing-requests/staffing-request/application-list-cta-buttons/active-cta-button';
import { ApplicationStateBadge } from 'works/legacy/components/application-state-badge';
import {
  PaginatedCandidateSubmissionsQueryResponse,
  PaginatedCandidateSubmissionsQueryVariables,
  StaffingRequestApplicationsType,
  SupplierStaffingRequestFiltersQuery,
} from '@applicant-flow/graphql/queries';
import { Button } from 'antd';
import { RenderShiftType } from '@applicant-flow/components';

const DEFAULT_STATUS_FILTERS = [
  {
    text: 'Submitted',
    value: 'submitted',
  },
  {
    text: 'Offered',
    value: 'offer',
  },
  {
    text: 'In Review',
    value: 'in_review',
  },
  {
    text: 'Accepted',
    value: 'accepted',
  },
  {
    text: 'Rejected',
    value: 'rejected',
  },
  {
    text: 'Withdrawn',
    value: 'withdrawn',
  },
];

const formatApplication = (application: StaffingRequestApplicationsType) => {
  const worksProfile = application.worksProfile;
  const staffingRequest = application.staffingRequest;
  const organizationBusiness = staffingRequest.organizationBusiness;
  const startDate = moment(staffingRequest.startDate);

  return {
    organizationBusiness,
    application: application,
    job: staffingRequest,
    jobId: {
      id: staffingRequest.code,
      staffingRequestId: staffingRequest.id,
    },
    worksProfile,
    key: application.id,
    start: {
      startDate: startDate.format('MM/DD/YYYY'),
      length: staffingRequest.length,
    },
    state: application.state,
    stateChangedAt: application.stateChangedAt,
    offerState: application.activeOffer?.state,
    offerStateChangedAt: application.activeOffer?.stateChangedAt,
    unitInfo: {
      unitName: staffingRequest.organizationUnit.clinicalUnit.displayName,
      businessName: organizationBusiness.name,
    },
    role: {
      abbreviation: staffingRequest.role.abbreviation,
      shiftsPerWeek: staffingRequest.shiftsPerWeek,
      hoursPerShift: staffingRequest.hoursPerShift,
      shiftType: staffingRequest.shiftType,
      length: staffingRequest.length,
    },
    rejectionReasonDescription: application.rejectionReasonDescription,
    rejectionDetails: application.rejectionDetails,
  };
};

type StatusFilter = {
  text: string;
  value: string;
};

type Props = {
  data: StaffingRequestApplicationsType[];
  total?: number;
  isLoaded: boolean;
  fetchMore: FetchMoreType<
    PaginatedCandidateSubmissionsQueryResponse,
    PaginatedCandidateSubmissionsQueryVariables
  >;
  statusFilters: StatusFilter[];
  statusFilterKey?: string;
  localStorageFilterKey: string;
  isOfferTable?: boolean;
  isAcceptedTable?: boolean;
};

export function SubmissionsTable({
  data,
  total = 0,
  isLoaded,
  fetchMore,
  statusFilters,
  localStorageFilterKey,
  statusFilterKey = 'state',
  isOfferTable,
  isAcceptedTable,
}: Props) {
  const navigate = useNavigate();
  const [paginationLoading, setPaginationLoading] = useState(false);

  const {
    filters,
    paginationVariables,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey,
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      defaultPageSize: 10,
    },
    columnsDictionary: {
      name: 'NAME',
      jobId: 'JOB_ID',
      clinicalUnit: 'CLINICAL_UNIT',
      organizationBusiness: 'ORGANIZATION_BUSINESS',
      start: 'START',
      role: 'ROLE',
      state: 'STATUS',
      shiftType: 'SHIFT_TYPE',
      shift: 'SHIFT',
      length: 'LENGTH',
    },
  });

  useEffect(() => {
    setPaginationLoading(true);

    fetchMore({
      variables: paginationVariables,
      updateQuery: (_prev, { fetchMoreResult }) => {
        return fetchMoreResult;
      },
    }).then(() => setPaginationLoading(false));
  }, [paginationVariables, fetchMore]);

  const { error: filtersQueryError, data: filtersQueryData } = useQuery(
    SupplierStaffingRequestFiltersQuery,
    {
      skip: !isLoaded,
    }
  );

  const filtersDataReady = !!filtersQueryData;

  if (filtersQueryError) {
    return <ErrorHandler error={filtersQueryError} log="requests-table" />;
  }

  const formattedData = data?.map(formatApplication) || [];

  const columns = [
    {
      title: isAcceptedTable ? 'Team Member' : 'Candidate',
      dataIndex: 'worksProfile',
      key: 'name',
      width: 250,
      sorter: true,
      filtered: filters.name || false,
      filteredValue: filters.name || null,
      className: 'sentry-mask',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: string[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function RenderWorksProfile(worksUserInfo: {
        id: string;
        firstName: string;
        lastName: string;
        fullName: string;
      }) {
        return (
          <div>
            <WorksAvatar
              firstName={worksUserInfo.firstName}
              lastName={worksUserInfo.lastName}
              size={40}
            />
            <span style={{ marginLeft: 8 }} />
            <Link to={buildCandidateRoute(worksUserInfo.id)}>
              <Text size="lg" weight="bold" color="elm">
                {worksUserInfo.fullName}
              </Text>
            </Link>
            <style jsx>{`
              :global(a.candidate-name) {
                font-style: normal;
                font-weight: 600;
                font-size: 16px;
                line-height: 24px;
                margin-left: 10px;
                color: ${Colors.elm};
                vertical-align: middle;
              }
            `}</style>
          </div>
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'jobId',
      key: 'code',
      sorter: true,
      filtered: filters.code || false,
      filteredValue: filters.code || null,
      filterFormat: 'id',
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: string[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <TextFilterSearch
            onSearch={(value) => {
              value ? setSelectedKeys([value]) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: function RenderStaffingRequestId(jobId: {
        id: string;
        staffingRequestId: string;
      }) {
        const url = isOfferTable
          ? `/works/supplier/staffing-request/${jobId.staffingRequestId}/offers`
          : `/works/supplier/staffing-request/${jobId.staffingRequestId}`;

        return (
          <Button
            type="link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(url);
            }}
            className="text-medium"
          >
            {jobId.id}
          </Button>
        );
      },
    },
    {
      title: 'Location',
      dataIndex: 'organizationBusiness',
      key: 'organizationBusiness',
      filtered: filters.organizationBusiness || false,
      filteredValue: filters.organizationBusiness || null,
      filters: buildOptionsFromData(
        filtersQueryData?.organizationBusinesses || [],
        (orgBusiness: { id: string; name: string }) => ({
          value: orgBusiness.id,
          text: orgBusiness.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      render: (organizationBusiness: {
        name: string;
        city: string;
        stateCode: string;
      }) => (
        <>
          <div>
            <Text>{organizationBusiness.name}</Text>
          </div>
          <Text size="sm" color="midnightLighter">
            {`${organizationBusiness.city}, ${organizationBusiness.stateCode}`}
          </Text>
        </>
      ),

      sorter: true,
    },
    {
      title: 'Unit',
      dataIndex: ['unitInfo', 'unitName'],
      key: 'clinicalUnit',
      filtered: filters.clinicalUnit || false,
      filteredValue: filters.clinicalUnit || null,
      filters: buildOptionsFromData(
        filtersQueryData?.worksClinicalUnits || [],
        (clinicalUnit: { id: string; displayName: string }) => ({
          value: clinicalUnit.id,
          text: clinicalUnit.displayName,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      sorter: true,
    },
    {
      title: 'Role',
      dataIndex: ['role', 'abbreviation'],
      key: 'role',
      width: '80px',
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(
        filtersQueryData?.roles || [],
        (role: { id: string; name: string }) => ({
          value: role.id,
          text: role.name,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
      sorter: true,
    },
    {
      title: 'Shift Type',
      dataIndex: ['role', 'shiftType'],
      key: 'shiftType',
      filtered: filters.shiftType || false,
      filteredValue: filters.shiftType || null,
      filterDropdown: MultiSelectTableFilter,
      filters: [
        {
          text: <RenderShiftType shiftType="day" />,
          value: 'day',
        },
        {
          text: <RenderShiftType shiftType="evening" />,
          value: 'evening',
        },
        {
          text: <RenderShiftType shiftType="night" />,
          value: 'night',
        },
        {
          text: <RenderShiftType shiftType="variable" />,
          value: 'variable',
        },
      ],
      width: '120px',
      sorter: true,
      render: (shiftType: 'day' | 'evening' | 'night' | 'variable') => (
        <RenderShiftType shiftType={shiftType} />
      ),
    },
    {
      title: 'Shift',
      dataIndex: 'id',
      key: 'shift',
      width: '60px',
      sorter: true,
      render: (
        _: string,
        application: { role: { hoursPerShift: number; shiftsPerWeek: number } }
      ) => {
        const { role } = application;
        return `${role.shiftsPerWeek}x${role.hoursPerShift}`;
      },
    },
    {
      title: 'Length',
      dataIndex: ['role', 'length'],
      key: 'length',
      width: '90px',
      sorter: true,
      filtered: filters.length || false,
      filteredValue: filters.length || null,
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: number[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <NumberRangeFilter
            onSearch={(values) => {
              values ? setSelectedKeys(values) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      render: (length: string) => `${length} weeks`,
    },
    {
      title: 'Start',
      dataIndex: ['start', 'startDate'],
      key: 'startDate',
      filterFormat: 'dateRange',
      filtered: filters.startDate || false,
      filteredValue: filters.startDate || null,
      filterDropdown: ({
        setSelectedKeys,
        confirm,
      }: {
        setSelectedKeys: (values: (string | Moment | null)[]) => void;
        confirm: () => void;
      }) => (
        <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
          <DateRangeFilter
            autoFocus
            onSearch={(value) => {
              value ? setSelectedKeys(value) : setSelectedKeys([]);
              confirm();
            }}
          />
        </section>
      ),
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'state',
      width: '190px',
      key: statusFilterKey,
      filtered: filters[statusFilterKey] || false,
      filteredValue: filters[statusFilterKey] || null,
      filters: statusFilters || DEFAULT_STATUS_FILTERS,
      filterDropdown: MultiSelectTableFilter,
      render: (state: string, submission: (typeof formattedData)[0]) => (
        <>
          <ApplicationStateBadge
            state={state}
            reason={submission.rejectionReasonDescription}
            rejectionDetails={submission.rejectionDetails}
            timestamp={submission.stateChangedAt}
            offerState={submission.offerState}
            offerTimestamp={submission.offerStateChangedAt}
          />
        </>
      ),
      sorter: true,
      defaultSortOrder: 'ascend',
    },
    {
      key: 'buttonInfo',
      render: (submission: (typeof formattedData)[0]) => (
        <ActiveCtaButton
          staffingRequestId={submission.job.id}
          application={submission.application}
        />
      ),
    },
  ];

  return (
    <FilteredTable
      columns={columns}
      rowKey="jobId"
      dataSource={formattedData}
      loading={!isLoaded || paginationLoading}
      pagination={{
        ...paginationVariables,
        total,
      }}
      onChange={tableChangeHandler}
      onFilterChange={filterChangeHandler}
      filtersDataReady={filtersDataReady}
      localStorageFilterKey={localStorageFilterKey}
      locale={{
        emptyText:
          'We’ll notify you of new submissions as they become available for your department.',
      }}
      filters={undefined}
    />
  );
}

function buildCandidateRoute(id: string) {
  return `/works/supplier/candidates/${id}`;
}
